import { Button, Input, makeStyles, Tooltip, useAnnounce } from '@fluentui/react-components';
import { ArrowNext20Regular, ArrowPrevious20Regular, ChevronLeft20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormView } from '../../contexts/useFormView';
import { ReviewFormDataType } from '../../model';
import { isValidIntermediateNumberInput } from '../../utils/NumberUtils';
import { findTocItemById } from '../../utils/TaxReturnTOCUtils';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '5px',
    },
    pageControls: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    pageInput: {
        width: '50px',
        textAlign: 'center',
    },
    ofPages: {
        marginLeft: '8px',
    },
});

export interface IFormPageNavigationProps {
    isFirstForm: boolean;
    isLastForm: boolean;
    onNavigateToPreviousForm: (goToLastPage?: boolean) => void;
    onNavigateToNextForm: () => void;
    isIsolatedTocItem?: boolean;
}

export const FormPageNavigation: React.FC<IFormPageNavigationProps> = ({
    isFirstForm,
    isLastForm,
    onNavigateToPreviousForm,
    onNavigateToNextForm,
    isIsolatedTocItem = false,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { announce } = useAnnounce();
    const {
        currentTOCItemState,
        currentFormTotalPages,
        formDataType,
        showForm,
        isLoading,
        tocData,
        hasError,
        pdfSyncPhase,
        navigateToPageNumber,
        navigateToRelativePageNumber,
        navigateToFirstPage,
        navigateToLastPage,
    } = useFormView();

    const isHtmlContent = formDataType === ReviewFormDataType.Html;
    const isPdfReady = isHtmlContent || !isIsolatedTocItem || pdfSyncPhase === 'ready';

    const totalPages = isHtmlContent ? 1 : isPdfReady ? currentFormTotalPages : 1;

    const currentPageNumber: number = isHtmlContent ? 1 : isPdfReady ? (currentTOCItemState?.pageIndex ?? 0) + 1 : 1;

    const isFirstPage: boolean = currentPageNumber <= 1;
    const isLastPage: boolean = currentPageNumber >= totalPages;

    const [currentPageInput, setCurrentPageInput] = useState<string>(currentPageNumber.toString());

    useEffect(() => {
        setCurrentPageInput(currentPageNumber.toString() || '');
    }, [currentTOCItemState?.id, formDataType, pdfSyncPhase, currentPageNumber]);

    const handleInputOnChange = (_event: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
        const inputValue = data.value;
        if (!inputValue) {
            setCurrentPageInput('');
            return;
        }
        if (isValidIntermediateNumberInput(inputValue)) {
            setCurrentPageInput(inputValue);
        }
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleNavigateToPage();
        }
    };

    const handleNavigateToPage = () => {
        const pageNumber: number = parseInt(currentPageInput);
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            navigateToPageNumber(pageNumber);
            return;
        }

        setCurrentPageInput(currentPageNumber.toString());
    };

    const previousPage = () => {
        if (!isFirstPage) {
            navigateToRelativePageNumber(-1);
        } else if (!isFirstForm) {
            onNavigateToPreviousForm(true);
        }
    };

    const nextPage = () => {
        if (!isLastPage) {
            navigateToRelativePageNumber(1);
        } else if (!isLastForm) {
            onNavigateToNextForm();
        }
    };

    const goToFirstPage = () => {
        if (!isFirstPage) {
            navigateToFirstPage();
        }
    };

    const goToLastPage = () => {
        if (!isLastPage) {
            navigateToLastPage();
        }
    };

    const getPageNumberAccesibilityText = (): string => {
        return t('pageXOfY', { pageNumber: currentPageNumber, totalPages });
    };

    const shouldDisablePageNavigationButtons: boolean = isLoading || hasError;
    const shouldDisableFirstPageButton: boolean = isFirstPage || shouldDisablePageNavigationButtons;
    const shouldDisablePreviousPageButton: boolean = (isFirstPage && isFirstForm) || shouldDisablePageNavigationButtons;
    const shouldDisableNextPageButton: boolean = (isLastPage && isLastForm) || shouldDisablePageNavigationButtons;
    const shouldDisableLastPageButton: boolean = isLastPage || shouldDisablePageNavigationButtons;

    const getAnnouncedMessage = () => {
        const parentTitle: string | null | undefined = findTocItemById(tocData?.items, currentTOCItemState?.parentId)?.title;
        const tocItemTitle = currentTOCItemState?.tocItemTitle || '';
        if (parentTitle) {
            return `${t('form')}: ${parentTitle} ${t('page')}: ${tocItemTitle}`;
        }
        return `${t('form')}: ${tocItemTitle}`;
    };

    const announcedMessage = showForm && currentTOCItemState ? getAnnouncedMessage() : '';

    useEffect(() => {
        if (!announcedMessage) {
            return;
        }
        announce(announcedMessage, { batchId: 'form-page-nav' });
    }, [announcedMessage, announce]);

    return (
        <div className={styles.root}>
            <Tooltip content={t('firstPage').toString()} relationship='description' appearance='inverted' withArrow positioning='above'>
                <Button
                    appearance='subtle'
                    icon={<ArrowPrevious20Regular />}
                    aria-label={t('goToFirstPageForm').toString()}
                    onClick={goToFirstPage}
                    disabled={shouldDisableFirstPageButton}
                    disabledFocusable={shouldDisableFirstPageButton}
                />
            </Tooltip>
            <Tooltip content={t('previousPage').toString()} relationship='description' appearance='inverted' withArrow positioning='above'>
                <Button
                    appearance='subtle'
                    icon={<ChevronLeft20Regular />}
                    aria-label={t('previousPage').toString()}
                    onClick={previousPage}
                    disabled={shouldDisablePreviousPageButton}
                    disabledFocusable={shouldDisablePreviousPageButton}
                />
            </Tooltip>
            <Tooltip content={t('nextPage').toString()} relationship='description' appearance='inverted' withArrow positioning='above'>
                <Button
                    appearance='subtle'
                    icon={<ChevronRight20Regular />}
                    aria-label={t('nextPage').toString()}
                    onClick={nextPage}
                    disabled={shouldDisableNextPageButton}
                    disabledFocusable={shouldDisableNextPageButton}
                />
            </Tooltip>
            <Tooltip content={t('lastPage').toString()} relationship='description' appearance='inverted' withArrow positioning='above'>
                <Button
                    appearance='subtle'
                    icon={<ArrowNext20Regular />}
                    aria-label={t('goToLastPageForm').toString()}
                    onClick={goToLastPage}
                    disabled={shouldDisableLastPageButton}
                    disabledFocusable={shouldDisableLastPageButton}
                />
            </Tooltip>
            <Tooltip
                content={getPageNumberAccesibilityText()}
                relationship='description'
                appearance='inverted'
                withArrow
                positioning='above'
            >
                <span>
                    <div className={styles.pageControls}>
                        <Input
                            value={hasError ? '0' : currentPageInput}
                            onChange={handleInputOnChange}
                            aria-label={getPageNumberAccesibilityText()}
                            onKeyUp={handleKeyUp}
                            onBlur={handleNavigateToPage}
                            className={styles.pageInput}
                            input={{ className: styles.pageInput }}
                            disabled={shouldDisablePageNavigationButtons}
                            role='input'
                        />
                        <div className={styles.ofPages}>{t('ofXPages', { totalPages })}</div>
                    </div>
                </span>
            </Tooltip>
        </div>
    );
};
